<script>
import { useVuelidate } from '@vuelidate/core';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import TagMultiSelectComboBox from 'next/combobox/TagMultiSelectComboBox.vue';
import IconNext from 'next/icon/Icon.vue';

export default {
  components: {
    TagMultiSelectComboBox,
    IconNext,
  },
  props: {
    values: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    valueType: {
      type: String,
      default: 'string',
    },
    options: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line vue/no-unused-properties
    deselectLabel: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      multiselectValues: this.values ?? this.defaultValue,
    };
  },
  computed: {
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      multiselectValues: {
        required: multiselectValues => {
          if (!this.required) return true;

          return multiselectValues.length > 0;
        },
        invalidOption: multiselectValues => {
          if (!multiselectValues || multiselectValues.length === 0) return true;

          const allOptionValues = this.options.map(opt => opt?.value ?? opt);
          return multiselectValues.every(val => allOptionValues.includes(val));
        },
      },
    };
  },
  watch: {
    values(values) {
      this.multiselectValues = values;
    },
    options() {
      this.multiselectValues = this.values;
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    applyValueType(value) {
      if (this.valueType === 'number') {
        return Number(value);
      }
      if (this.valueType === 'boolean') {
        return value === 'true';
      }
      if (this.valueType === 'date') {
        return new Date(value);
      }
      return value;
    },
    updateValue(values) {
      const updatedValues = values.map(value => this.applyValueType(value));
      this.multiselectValues = updatedValues;
      this.v$.multiselectValues.$touch();
      this.$emit('change', updatedValues);
    },
    closeMultiselect() {
      this.$refs.multiselection.deactivate();
    },
  },
};
</script>

<template>
  <div
    class="relative flex flex-col min-w-0 gap-1"
    :class="{ error: v$.multiselectValues.$error }"
  >
    <label
      v-if="label || descriptionTooltip"
      class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
    >
      <div v-if="label" class="block truncate">
        {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
      </div>
      <IconNext
        v-if="descriptionTooltip"
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <TagMultiSelectComboBox
      v-model="multiselectValues"
      :options="options"
      :placeholder="toLocale(placeholder)"
      :has-error="v$.multiselectValues.$error"
      :disabled="disabled"
      class="[&>div>button]:bg-n-alpha-black2 [&>div>button]:!h-6 [&>div>button]:!min-h-6"
      @update:model-value="updateValue"
    />

    <p
      v-if="v$.multiselectValues.$error"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      <template v-if="v$.multiselectValues.required.$invalid">
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED') }}
      </template>
      <template v-else-if="v$.multiselectValues.invalidOption.$invalid">
        {{
          $t(
            'WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.OPTION_NOT_FOUND',
            {},
            { default: $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID') }
          )
        }}
      </template>
      <template v-else>
        {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID') }}
      </template>
    </p>
  </div>
</template>

<style lang="scss" scope>
.multiselect {
  .multiselect__tags {
    @apply min-h-[2.5rem];
    z-index: 10;
  }
  .multiselect__content-wrapper {
    z-index: -1;
  }
}
</style>
